import { CheckCircle2, Circle, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useActivity, useIssuedReports, useNotificationChannels, useOverview, useSession } from "../api/queries";
import { useT } from "../i18n/core";
import { Panel } from "../ui/Common";
import { helpMessages } from "./help.i18n";

// Primeiros passos (ADR 0027): o "assistente" do primeiro acesso. Cada passo
// é marcado pelo estado real da organização, não por clique; o painel some
// quando tudo está feito ou quando a pessoa o oculta neste navegador.

export type StepKey = "agent" | "policy" | "backup" | "restore" | "alerts" | "report";

const ROUTE: Record<StepKey, string> = {
  agent: "/add-machines", policy: "/policies", backup: "/machines", restore: "/machines", alerts: "/settings", report: "/reports",
};

const hiddenKey = (orgId: string) => `ebk-first-steps:${orgId}`;

function readHidden(orgId: string): boolean {
  try {
    return localStorage.getItem(hiddenKey(orgId)) === "1";
  } catch {
    return false;
  }
}

/** Passos e se cada um está feito, a partir do que a console já sabe. */
export function computeSteps(input: {
  counts: { total: number; revoked: number; protected: number; stale: number };
  recentJobs: { kind: string; status: string; endedAt: string | null }[];
  activity: { kind: string; status: string }[];
  channels: number | null;
  reports: number | null;
}): { key: StepKey; done: boolean }[] {
  const machines = input.counts.total - input.counts.revoked > 0;
  const backup = input.counts.protected + input.counts.stale > 0
    || input.recentJobs.some((j) => j.kind === "backup" && j.endedAt !== null && j.status !== "failed");
  const restore = input.activity.some((j) => j.kind === "restore" && (j.status === "succeeded" || j.status === "warning"));
  const steps: { key: StepKey; done: boolean }[] = [
    { key: "agent", done: machines },
    // A política padrão conta como conferida quando já produziu um backup.
    { key: "policy", done: backup },
    { key: "backup", done: backup },
    { key: "restore", done: restore },
  ];
  // Canais de notificação só existem para quem administra a organização; para
  // os demais o passo não aparece, em vez de aparecer como impossível.
  if (input.channels !== null) steps.push({ key: "alerts", done: input.channels > 0 });
  if (input.reports !== null) steps.push({ key: "report", done: input.reports > 0 });
  return steps;
}

export function FirstSteps({ orgId }: { orgId: string }) {
  const session = useSession();
  const admin = session.data?.user.role === "org_admin" || session.data?.user.platformAdmin === true;
  const overview = useOverview(orgId);
  const activity = useActivity(orgId);
  const channels = useNotificationChannels(orgId, admin);
  const reports = useIssuedReports(orgId);
  const t = useT(helpMessages);
  const [hidden, setHidden] = useState(() => readHidden(orgId));

  // Só aparece com tudo carregado, para não piscar "5 de 6" antes da resposta das outras consultas.
  if (hidden || !overview.data || activity.isPending || reports.isPending || (admin && channels.isPending)) return null;

  const steps = computeSteps({
    counts: overview.data.counts,
    recentJobs: overview.data.recentJobs,
    activity: activity.data ?? [],
    channels: admin ? (channels.data?.channels.length ?? 0) : null,
    reports: reports.isError ? null : (reports.data?.length ?? 0),
  });
  const done = steps.filter((s) => s.done).length;
  if (done === steps.length) return null;

  function hide() {
    try {
      localStorage.setItem(hiddenKey(orgId), "1");
    } catch {
      /* só nesta visita */
    }
    setHidden(true);
  }

  return (
    <Panel
      title={t.steps.title}
      sub={t.steps.sub(done, steps.length)}
      actions={
        <div style={{ display: "flex", gap: 8 }}>
          <Link className="btn btn-sm" to={`/o/${orgId}/help/first-steps`}>{t.steps.guide}</Link>
          <button type="button" className="btn btn-ghost btn-sm" onClick={hide}><X aria-hidden="true" /> {t.steps.hide}</button>
        </div>
      }
      bodyless
    >
      <div className="first-steps-progress" role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={steps.length} aria-label={t.steps.sub(done, steps.length)}>
        <span style={{ width: `${(done / steps.length) * 100}%` }} />
      </div>
      <ol className="first-steps">
        {steps.map((s) => {
          const item = t.steps.items[s.key];
          return (
            <li key={s.key} data-done={s.done}>
              {s.done ? <CheckCircle2 className="mark" aria-hidden="true" /> : <Circle className="mark" aria-hidden="true" />}
              <div className="what">
                <strong>{item.title}</strong>
                {!s.done && <span>{item.body}</span>}
              </div>
              {!s.done && <Link className="btn btn-sm" to={`/o/${orgId}${ROUTE[s.key]}`}>{item.action}</Link>}
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}
